import api from './client';
import { DailyNutritionalSummary, FeedMixComponent } from './nutrition';

export interface FeedMixTemplate {
  id: string;
  name: string;
  description?: string;
  category: 'breeding' | 'maintenance' | 'molting' | 'juvenile' | 'custom';
  components: FeedMixComponent[];
  nutritionalSummary: DailyNutritionalSummary;
  metadata: {
    createdBy: string;
    useCount: number;
    rating: number;
    tags: string[];
    isPublic: boolean;
    costPerKg: number;
    targetBirdCount?: number;
    lifestage?: string;
    notes?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateFeedMixTemplateDto {
  name: string;
  description?: string;
  category?: FeedMixTemplate['category'];
  components: FeedMixComponent[];
  nutritionalSummary: DailyNutritionalSummary;
  metadata?: Partial<FeedMixTemplate['metadata']>;
}

export interface UpdateFeedMixTemplateDto {
  name?: string;
  description?: string;
  category?: FeedMixTemplate['category'];
  components?: FeedMixComponent[];
  nutritionalSummary?: DailyNutritionalSummary;
  metadata?: Partial<FeedMixTemplate['metadata']>;
}

function mapTemplate(template: FeedMixTemplate): FeedMixTemplate {
  return {
    ...template,
    metadata: {
      createdBy: template.metadata?.createdBy ?? 'system',
      useCount: template.metadata?.useCount ?? 0,
      rating: template.metadata?.rating ?? 0,
      tags: template.metadata?.tags ?? [],
      isPublic: template.metadata?.isPublic ?? false,
      costPerKg: template.metadata?.costPerKg ?? 0,
      targetBirdCount: template.metadata?.targetBirdCount,
      lifestage: template.metadata?.lifestage,
      notes: template.metadata?.notes,
    },
  };
}

export const feedMixTemplatesApi = {
  async getAll(category?: string): Promise<FeedMixTemplate[]> {
    const params = category && category !== 'all' ? `?category=${encodeURIComponent(category)}` : '';
    const response = await api.get<FeedMixTemplate[]>(`/feed-stock/nutrition/mix-templates${params}`);
    return (response.data ?? []).map(mapTemplate);
  },

  async create(data: CreateFeedMixTemplateDto): Promise<FeedMixTemplate> {
    const response = await api.post<FeedMixTemplate>('/feed-stock/nutrition/mix-templates', data);
    return mapTemplate(response.data);
  },

  async update(id: string, data: UpdateFeedMixTemplateDto): Promise<FeedMixTemplate> {
    const response = await api.put<FeedMixTemplate>(`/feed-stock/nutrition/mix-templates/${id}`, data);
    return mapTemplate(response.data);
  },

  async recordUse(id: string): Promise<FeedMixTemplate> {
    const response = await api.post<FeedMixTemplate>(`/feed-stock/nutrition/mix-templates/${id}/use`);
    return mapTemplate(response.data);
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/feed-stock/nutrition/mix-templates/${id}`);
  },
};

export default feedMixTemplatesApi;
